<template>
  <div
    class="oct-form-group"
    :class="[{
      'oct-form-group--with-caption': $scopedSlots['caption'],
      'oct-form-group--nowrap': nowrap,
      'oct-form-group--dense': dense
    }]"
  >
    <div class="oct-form-group__content">
      <div class="oct-form-group__label" v-if="label">
        <h3 class="oct-form-group__label-text">
          {{ label }}

          <!-- Render help content if helper slot is sat -->
          <div
            class="oct-form-group-helper"
            v-if="$slots.helper"
            @mouseenter="openPopover(helperId, $event.currentTarget)"
            @mouseleave="closePopover(helperId)"
            @click="$event.stopPropagation()"
          >
            <!-- Help Icon -->
            <oct-icon
              class="oct-form-group-helper__icon"
              icon="help-sign"
            />

            <!-- Render popover -->
            <oct-popover
              :id="helperId"
            >
              <slot name="helper" />
            </oct-popover>
          </div>
        </h3>
        <span class="oct-form-group__meta-text" v-if="meta !== ''">{{ meta }}</span>
      </div>

      <div class="oct-form-group__row" v-if="$scopedSlots.default">
        <slot />

        <div class="oct-form-group__text" v-if="$scopedSlots['text']">
          <slot name="text"/>
        </div>
      </div>

      <div class="oct-form-group__caption" v-if="$scopedSlots['caption']">
        <slot name="caption"/>
      </div>
    </div>
  </div>
</template>

<script>
import OctIcon from '@/components/icon/OctIcon.vue';
import OctPopover, {useOctPopover}  from '@/components/popover/OctPopover.vue';

/**
 * Destructures the `open` and `close` methods from the `useOctPopover` composable.
 * 
 * @constant {Function} openPopover - Function to open the popover.
 * @constant {Function} closePopover - Function to close the popover.
 */
const {open: openPopover, close: closePopover} = useOctPopover();

export default {
  name: 'OctFormGroup',
  components: {
    OctIcon,
    OctPopover
  },
  props: {
    /** Label text */
    label: {
      type: String,
      default: ''
    },
    /** Label text */
    meta: {
      type: String,
      default: ''
    },
    /** Size of margin to the next form group */
    dense: {
      type: Boolean,
      default: false
    },
    /** Fex's wrap style */
    nowrap: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    /** Generate random ID for the helper popover */
    helperId () {
      return `oct-button-helper--${Math.random().toString(36).substring(2)}`
    }
  },
  methods: {
    openPopover,
    closePopover
  }
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-form-group {
  margin: 0 0 oct-rem(20);

  &:last-child {
    margin-bottom: 0;
  }

  ::v-deep .oct-text-field {
    width: 100%;
  }

  ::v-deep .oct-text-field__input[size="40"],
  ::v-deep .oct-text-field__input[cols="40"] {
    width: 100%;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: 0 0 oct-rem(24);
}
}

.oct-form-group__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: oct-rem(44);

  .oct-form-group--with-caption & {
    margin-bottom: oct-rem(6)
  }

  .oct-form-group--nowrap & {
    flex-wrap: nowrap;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  min-height: oct-rem(48);
}
}

.oct-form-group__label {
  margin: 0 0 oct-rem(12);
  display: flex;
  justify-content: space-between;
}

.oct-form-group__label-text,
.oct-form-group__meta-text {
  font-size: oct-rem(13);
  font-weight: normal;
  line-height: (18/13);
  margin: (-3em/13) 0;
  display: block;
  min-height: oct-rem(18);
}

.oct-form-group__meta-text {
  opacity: .55;
}

.oct-form-group__text {
  font-size: oct-rem(18);
  font-weight: normal;
  line-height: (24/18);
  margin: (-5em/18) 0 (-5em/18) (12em/18);
  display: block;
}

.oct-form-group__caption {
  font-size: oct-rem(13);
  font-weight: normal;
  line-height: (18/13);
  color: $oct-theme--secondary;
  margin: (-3em/13) 0;
  display: block;
  width: 100%;
  -webkit-font-smoothing: antialiased;

  a {
    color: $oct-theme--primary;
  }
}

/* Helper */
.oct-form-group-helper {
  position: relative;
  margin-left: oct-rem(4);
  display: none;
  align-items: center;
  vertical-align: top;

  &:hover {
    // Update the z-index to ensure the popover is on top
    z-index: 1;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  display: inline-flex;
}
}

.oct-form-group-helper__icon {
  width: oct-rem(14);
  height: oct-rem(14);
  opacity: .4;
}

</style>
